import { useState, useEffect } from "react";
import { Button, Textarea } from "@heroui/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { EmailSettings } from "./types";

interface EmailSettingsSectionProps {
  onSettingsChange?: (settings: EmailSettings | null) => void;
}

export const EmailSettingsSection: React.FC<EmailSettingsSectionProps> = ({
  onSettingsChange
}) => {
  const { session } = useSession();
  const [headerHtml, setHeaderHtml] = useState("");
  const [footerHtml, setFooterHtml] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/settings`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const settings = await response.json();
        // Check if settings exist (has id property) or is empty object
        if (settings && settings.id) {
          setHeaderHtml(settings.headerHtml || "");
          setFooterHtml(settings.footerHtml || "");
          onSettingsChange?.(settings);
        } else {
          // No settings exist yet (empty object returned)
          setHeaderHtml("");
          setFooterHtml("");
          onSettingsChange?.(null);
        }
      }
    } catch (error) {
      console.error("Error fetching email settings:", error);
      console.error("Response details:", {
        url: `${API_URL}/mail-templates/settings`,
        error: error instanceof Error ? error.message : String(error)
      });
      toast.error("Fout bij het ophalen van e-mail instellingen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        headerHtml: headerHtml.trim() || null,
        footerHtml: footerHtml.trim() || null
      };
      
      console.log('Sending email settings payload:', {
        headerHtml: payload.headerHtml ? 'Present' : 'Null',
        footerHtml: payload.footerHtml ? 'Present' : 'Null',
        headerLength: payload.headerHtml?.length || 0,
        footerLength: payload.footerHtml?.length || 0
      });
      
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/settings`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        session
      );

      if (response.ok) {
        const updatedSettings = await response.json();
        toast.success("E-mail instellingen succesvol opgeslagen");
        onSettingsChange?.(updatedSettings);
      } else {
        // Get detailed error information
        let errorMessage = "Fout bij opslaan van e-mail instellingen";
        try {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          if (errorData.message) {
            errorMessage = `Fout: ${errorData.message}`;
          }
        } catch (e) {
          console.error('Could not parse error response');
        }
        console.error('Response status:', response.status, response.statusText);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error saving email settings:", error);
      toast.error("Fout bij opslaan van e-mail instellingen");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-neutral-primary">E-mail Header & Footer</h3>
          <p className="text-sm text-neutral-secondary-mid">
            Globale HTML header en footer die aan alle e-mail sjablonen worden toegevoegd
          </p>
        </div>
        <Button
          className="bg-moss-primary text-white"
          onPress={handleSave}
          isLoading={isSaving}
        >
          Opslaan
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Header HTML */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-neutral-primary">
              Header HTML
            </label>
            <span className="text-xs text-neutral-secondary-mid">
              Optioneel
            </span>
          </div>
          <Textarea
            placeholder="<div>Uw bedrijf naam<br/>Adres en contactgegevens</div>"
            value={headerHtml}
            onChange={(e) => setHeaderHtml(e.target.value)}
            minRows={6}
            maxRows={10}
            description="HTML die bovenaan elke e-mail wordt getoond"
            isDisabled={isLoading}
          />
        </div>

        {/* Footer HTML */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-neutral-primary">
              Footer HTML
            </label>
            <span className="text-xs text-neutral-secondary-mid">
              Optioneel
            </span>
          </div>
          <Textarea
            placeholder="<div>Met vriendelijke groeten,<br/><strong>Het Yally Team</strong></div>"
            value={footerHtml}
            onChange={(e) => setFooterHtml(e.target.value)}
            minRows={6}
            maxRows={10}
            description="HTML die onderaan elke e-mail wordt getoond"
            isDisabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}; 